<template>
  <div v-if="totalPages > 1" class="flex items-center justify-between px-8 py-6 bg-gray-50/50 border-t border-gray-100">
    <!-- Info -->
    <div class="text-sm text-gray-600">
      <span class="font-bold text-gray-900">{{ startItem }}-{{ endItem }}</span>
      <span class="mx-1">sur</span>
      <span class="font-bold text-gray-900">{{ totalItems }}</span>
      <span class="ml-1">{{ itemLabel }}</span>
    </div>

    <!-- Pagination Controls -->
    <div class="flex items-center gap-2">
      <!-- Première page -->
      <button
        @click="goToPage(1)"
        :disabled="currentPage === 1"
        :class="[
          'px-3 py-2 rounded-xl font-bold text-sm transition-all',
          currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
        ]"
        title="Première page"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

      <!-- Précédent -->
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1"
        :class="[
          'px-4 py-2 rounded-xl font-bold text-sm transition-all',
          currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
        ]"
      >
        Précédent
      </button>

      <!-- Numéros de page -->
      <div class="flex items-center gap-1">
        <button
          v-for="page in visiblePages"
          :key="page"
          @click="page !== '...' && goToPage(page)"
          :class="[
            'min-w-[40px] h-10 rounded-xl font-bold text-sm transition-all',
            page === currentPage
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
              : page === '...'
              ? 'text-gray-400 cursor-default'
              : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
          ]"
          :disabled="page === '...'"
        >
          {{ page }}
        </button>
      </div>

      <!-- Suivant -->
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        :class="[
          'px-4 py-2 rounded-xl font-bold text-sm transition-all',
          currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
        ]"
      >
        Suivant
      </button>

      <!-- Dernière page -->
      <button
        @click="goToPage(totalPages)"
        :disabled="currentPage === totalPages"
        :class="[
          'px-3 py-2 rounded-xl font-bold text-sm transition-all',
          currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
        ]"
        title="Dernière page"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Items par page -->
    <div class="flex items-center gap-2">
      <label class="text-sm text-gray-600 font-medium">Afficher :</label>
      <select
        :value="itemsPerPage"
        @change="$emit('update:itemsPerPage', Number($event.target.value))"
        class="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all"
      >
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  totalItems: {
    type: Number,
    required: true
  },
  itemsPerPage: {
    type: Number,
    default: 25
  },
  itemLabel: {
    type: String,
    default: 'éléments'
  }
});

const emit = defineEmits(['update:currentPage', 'update:itemsPerPage']);

const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage));

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return end > props.totalItems ? props.totalItems : end;
});

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = props.currentPage;

  if (total <= 7) {
    // Afficher toutes les pages si <= 7
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Toujours afficher la première page
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    // Pages autour de la page courante
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }

    // Toujours afficher la dernière page
    pages.push(total);
  }

  return pages;
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    emit('update:currentPage', page);
  }
};
</script>
